define([
	"dojo/_base/declare",
	"src/c8-platform/core/request",
	"csi/action/NewFileController",
	"csi/comm/Aspera",
	"csi/comm/PostRequest",
	"csi/html",
	"csi/locale",
	"csi/state/rootStore",
	"csi/table/NewMixin"
], function (declare, { REQUEST_HANDLER }, NewFileController, Aspera, PostRequest, html, locale, { browserStore }, NewMixin) {

	return declare([NewFileController, NewMixin], {

		allowPartialUpload: true,

		sendFilesOnProcess: false,

		bEditHeading: false,

		// String: the name of the operation that will be uploaded to
		operationName: "CreateDocumentNodes",

		/** @property {boolean} bInterrupt - set to true so that controller is stopped on next action when user cancels file browser */
		bInterrupt: true,

		/** @property {boolean} canceledFileSelect - true when user cancels Aspera file select */
		canceledFileSelect: false,

		/** @property {array} resultURLs - array of document CNLs that need to have their files published */
		resultURLs: [],

		getNewNodes: function () {
			// summary: return nodes wrapping uploaded files
			return this.getNewFileNodes();
		},

		/**
		 * @override
		 * Select files to upload
		 */
		onPreActionDeferreds: function (dfds) {
			// Aspera has its own file select dialog, we must use it instead for Aspera except for drag & drop.

			if (this.droppedFiles) {
				this.actionValue = this.droppedFiles;
			} else if (!Aspera.isEnabled()) {
				dfds.push(html.selectFiles().then(files => {
					this.actionValue = files;
				}));
			}
		},

		preProcess: function () {
			// Use aspera for upload if enabled and allowed on the widget
			if (Aspera.isEnabled() && this.droppedFiles == null) {
				this.disableActionView(true);
				this.canceledFileSelect = false;
				Aspera.uploadFile(this.widget.urlRoot, null, false, true, this).then(
					(response) => {
						this._onPublishResponse(response);
					},
					(msg) => {
						// Do not show error if user cancels file selection, but show error if Aspera license is expired
						this.m_error = msg || "";
						if (!Aspera.isValid()) {
							this.canceledFileSelect = true;
						}
						this.postProcess(false);
					}
				);
				return;
			}

			// summary: call mixin before continuing
			if (this._newPreProcess()) {
				this.inherited(arguments);
			}
		},

		postProcessSuccess: function () {
			// summary: forward processing to mixin
			this._newPostProcess();
			return this.inherited(arguments);
		},

		getStringParameters: function () {
			return [locale.type(this.m_action.m_typeNode)];
		},

		/**
		 * @override
		 * If user cancels file selection, don't show error
		 */
		getError: function (suffix, params) {
			if (Aspera.isValid()) {
				return this.m_error;
			}
			return this.canceledFileSelect ? null : this.inherited(arguments);
		},

		/**
		 * @override
		 * Gets the CNLs for Document nodes that still need to have their files published
		 * @param {JsonResponse} response
		 */
		onPublishResponse: function (response) {
			// if the response has FileInfo data, this means that the files have already been published;
			// if using Aspera, then still going through one step upload process (create Document nodes as part of file upload)
			if ((this.droppedFiles == null && Aspera.isEnabled()) || response.data.FileInfo) {
				return;
			}

			this.resultURLs = response.getResultUrls();

			if (this.filesMap) {
				for (node of response.getNodes()) {
					const currentRev = this.getCache().getNode(node.CurrentRevision);
					const file = this.filesMap.get(currentRev["File.File"]);

					// Store document revision CNLs, their file size, and a corresponding abortController in the browserStore so 
					// that the file size can be displayed during upload		
					if (node.$Type == 'Document' && file) {
						browserStore.addToFileUploadMap(node.CurrentRevision, 'File', file.size, new AbortController());
					}
				}
			}
			this.droppedFiles = null;
		},

		/**
		 * @override
		 * Sends a second request that uploads the files after creating the document nodes from the first operation
		 */
		async onEnd() {
			if (this.resultURLs.length == 0) {
				return;
			}

			const files = this.actionValue;

			for (let i = 0; i < files.length; i++) {
				const postRequest = new PostRequest(this.moduleName, "PublishDocumentFiles")
					.addParameter("DocumentNodes", this.resultURLs[i])
					.addFiles(files[i], null, "FormFileFieldName");

				const docNode = this.getCache().getNode(this.resultURLs[i]);
				const formData = postRequest._createFormData(postRequest.content);
				const abortController = browserStore.getFileUploadController(docNode.CurrentRevision, 'File');
				const signal = abortController.signal;

				try {
					await fetch(REQUEST_HANDLER, {
						method: 'POST',
						body: formData,
						signal
					})
				} catch (err) {
					// AbortError may be thrown when aborting an upload. This is expected and the error should not be shown to the user
					if (err.name === "AbortError") {
						return;
					}

					this.displayErrorDialog({
						message: this.getError(),
						onResponse: () => { }
					});

					const docNode = this.m_cache.getNode(this.resultURLs[0]);
					this.widget.writeOperations((writer) => {
						writer.changeNode(docNode.CurrentRevision).deleteAttribute("File.Status");
					});
				}
			}
		},
	});
});